import { useEffect, useRef, useState } from 'react';
import { TranslationService } from '../../../services/translation.service';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import type { ITrafficSituation } from '../../../models/traffic-situation.model';
import TrafficSituationApiModel from '../models/traffic-situation.api-model';
import GeneralError from '../../Shared/GeneralError';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import ValidationInfo from '../../../validation/validation-info';
import TextFieldFormGroup from '../../Shared/TextFieldFormGroup';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import Spinner from '../../Shared/Spinner';
import AiEnhancedTrafficSituation from '../components/AiEnhancedTrafficSituation';
import LogService from '../../../services/log-service/log.service';

enum FeedbackOptions {
  Yes = 'yes',
  No = 'no',
  DontKnow = 'dontknow',
}

function processReceivedData(data: TrafficSituationApiModel[]): ITrafficSituation[] {
  return (data?.map((ts) => TrafficSituationApiModel.mapToBusinessModel(ts)) ?? []).filter((ts) => ts.aiGeneratedInformation);
}

function getRandomTrafficSituation(trafficSituations: ITrafficSituation[]): ITrafficSituation | null {
  if (trafficSituations.length === 0) return null;

  // Get a random index from the array
  const randomIndex = Math.floor(Math.random() * trafficSituations.length);
  return trafficSituations[randomIndex];
}

function TrafficSituationAiEnhancementEvaluation() {
  // === STATE ===
  const [currentTrafficSituation, setCurrentTrafficSituation] = useState<ITrafficSituation | null>(null);
  const [answer, setAnswer] = useState<string>(FeedbackOptions.Yes);
  const [comments, setComments] = useState<string>('');
  const [submitStatus, setSubmitStatus] = useState<Status>(Status.notperformed);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === FETCH ===
  const [getTrafficSituationsResult] = useFetch<TrafficSituationApiModel[]>('/api/trafficsituations/all');

  // === EFFECTS ===
  // Set a random traffic situation when data is loaded
  useEffect(() => {
    if (getTrafficSituationsResult.status === Status.success && getTrafficSituationsResult.data) {
      const trafficSituations = processReceivedData(getTrafficSituationsResult.data);
      setCurrentTrafficSituation(getRandomTrafficSituation(trafficSituations));
    }
  }, [getTrafficSituationsResult]);

  // === EVENT HANDLERS ===
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitStatus(Status.loading);
    try {
      const submitResponse = await fetch('/api/trafficsituations/ai-enhancement/evaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-XSRF-TOKEN': (document.getElementById('antiForgeryToken') as HTMLInputElement)?.value,
        },
        body: JSON.stringify({
          situationNumber: currentTrafficSituation?.situationNumber,
          answer: answer,
          comments: comments,
        }),
      });
      if (submitResponse.ok) {
        setSubmitStatus(Status.success);
        moveToNextTrafficSituation();
      } else {
        LogService.logError(`Error submitting traffic situation AI enhancement feedback. ${submitResponse.status} / ${submitResponse.statusText}`);
        setSubmitStatus(Status.failed);
      }
    } catch (error) {
      LogService.logError(error, 'Error submitting traffic situation AI enhancement feedback.');
      setSubmitStatus(Status.failed);
    }
  }

  function moveToNextTrafficSituation() {
    setAnswer(FeedbackOptions.Yes);
    setComments('');
    setSubmitStatus(Status.notperformed);
    if (getTrafficSituationsResult.data) {
      const trafficSituations = processReceivedData(getTrafficSituationsResult.data);
      setCurrentTrafficSituation(getRandomTrafficSituation(trafficSituations));
    }
  }

  // === RENDER ===
  if (getTrafficSituationsResult.status === Status.loading) return <Spinner className="my-4 my-md-5" />;

  if (getTrafficSituationsResult.status === Status.failed) return <GeneralError errorText={TranslationService.get('trafficsituationaievaluationpage.errorloading')} />;

  return (
    <div>
      <h2 className="visually-hidden">{TranslationService.get('trafficsituationaienhancementevaluationpage.alternativesheading')}</h2>
      <div className="row mb-4">
        <div className="col-md-6 mb-3 mb-md-0">
          <h3 className="h2 text-center">{TranslationService.get('trafficsituationaienhancementevaluationpage.beforeheading')}</h3>
          <div className="border border-gray-700 rounded p-3">
            <p>{currentTrafficSituation?.title ?? ''}</p>
            <p className="mb-0">{currentTrafficSituation?.description ?? ''}</p>
          </div>
        </div>
        <div className="col-md-6 mb-3 mb-md-0">
          <h3 className="h2 text-center">{TranslationService.get('trafficsituationaienhancementevaluationpage.afterheading')}</h3>
          <div className="border border-gray-700 rounded p-3">
            <AiEnhancedTrafficSituation trafficSituation={currentTrafficSituation!} />
          </div>
        </div>
      </div>

      <h2 className="visually-hidden">{TranslationService.get('trafficsituationaienhancementevaluationpage.answerheading')}</h2>
      <div className="feedback-form border rounded shadow-sm p-3">
        <form>
          <RadioFormGroup
            id="question"
            labelText={TranslationService.get('trafficsituationaienhancementevaluationpage.feedbackquestion')}
            rowLayout={true}
            disabled={submitStatus === Status.loading}
            options={[
              { value: FeedbackOptions.Yes, text: TranslationService.get('trafficsituationaienhancementevaluationpage.feedbackoptions.yes') },
              { value: FeedbackOptions.No, text: TranslationService.get('trafficsituationaienhancementevaluationpage.feedbackoptions.no') },
              { value: FeedbackOptions.DontKnow, text: TranslationService.get('trafficsituationaienhancementevaluationpage.feedbackoptions.dontknow') },
            ]}
            state={{ value: answer, validationInfo: new ValidationInfo() }}
            onChange={setAnswer}
          />
          <TextFieldFormGroup
            inputId="comments"
            labelText={TranslationService.get('trafficsituationaienhancementevaluationpage.commentsinstruction')}
            renderAsTextArea={true}
            disabled={submitStatus === Status.loading}
            state={{ value: comments, validationInfo: new ValidationInfo() }}
            onChange={setComments}
          />
          {submitStatus === Status.failed && <GeneralError className="mb-3" />}
          <HoneyPot ref={honeyPotRef} />
          <div>
            <div className="d-grid mx-auto col-md-7 mb-2">
              <button type="button" className="btn btn-primary me-sm-3" disabled={submitStatus === Status.loading} onClick={handleSubmit}>
                {TranslationService.get('trafficsituationaienhancementevaluationpage.submitbuttontext')}
              </button>
            </div>
            <div className="text-center">{TranslationService.get('trafficsituationaienhancementevaluationpage.textbelowsubmitbutton')}</div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrafficSituationAiEnhancementEvaluation;
